import { useEffect, useState } from 'react';
import type { GetStaticPropsContext, InferGetStaticPropsType, GetStaticPathsContext } from 'next';
import { NextSeo, ProductJsonLd } from 'next-seo';
import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer';
import { getSite } from '@lib/site';
import { getDictionary } from '@lib/dictionary';

import { getProduct, from_gql_id } from '@lib/product';


import { useTracking, useGeoIp } from '@hooks/index';

import { ProductPage } from '@components/product';
import {get_product_reviews} from '@components/ui'
import Custom404 from '@components/ui/404'

export default function Products({
  page,
  slug,
  site,
  locale,
}: InferGetStaticPropsType<typeof getStaticProps>) {

  if (!page) {
    return <Custom404 site={site}/>
  }

  const { trackPage } = useTracking();
  const geoIp = useGeoIp();

  const generateSeoTitle = () => {
    if (page?.color?.name) {
      return `${page.color.name} ${page.title}`;
    }

    return page.title;
  };

  useEffect(() => {
    if (geoIp?.country_code) {
      trackPage({
        name: generateSeoTitle(),
        category: page?.category?.name,
      });
    }
  }, [geoIp]);

  const [reviewData, setReviewData] = useState<any>({} as any)
  const everyVariantAvailable = page.shopifyProduct.variants.edges.every((edge: {node: {availableForSale: boolean}}) => edge.node.availableForSale)
  const oneVariantAvailable = page.shopifyProduct.variants.edges.findIndex((edge: {node: {availableForSale: boolean}}) => edge.node.availableForSale) !== -1

  useEffect(() => {
    if (page.shopifyProduct?.id) {
      const product_id = from_gql_id(page.shopifyProduct.id)
      get_product_reviews(product_id).then(res => setReviewData(res))
    }
  }, [page.shopifyProduct])

  return (
    <>
      <NextSeo
        title={generateSeoTitle()}
        titleTemplate={page.seoTitle ? page.seoTitle : ''}
        canonical={
          slug &&
          `https://www.hautehijab.com/${locale == 'en-US' ? '' : `${locale}/`}products/${slug}`
        }
        description={
          page.seoDescription || page?.description?.json?.content?.[0]?.content[0]?.value
        }
        openGraph={{
          images: [
            {
              url: page?.seoImage?.url,
              alt: page?.seoImage?.description,
            },
          ],
        }}
      />

      <ProductJsonLd
        productName={page.title}
        description={documentToPlainTextString(page.description?.json)}
        brand="Haute Hijab"
        color={page.color?.name}
        images={page.shopifyProduct.images.edges.map((image: {node: {altText: string, id: string, transformedSrc: string}}) => image.node.transformedSrc)}
        aggregateRating={reviewData?.total
        ? {
          ratingValue: reviewData?.rating,
          ratingCount: reviewData?.total
        } : undefined}
        offers={page.shopifyProduct.variants?.edges[0].node.presentmentPrices.edges.map((edge: any) => {
          return {
            price: edge.node.price.amount,
            priceCurrency: edge.node.price.currencyCode,
            itemCondition: 'https://schema.org/NewCondition',
            availability: everyVariantAvailable ? 'https://schema.org/InStock' : oneVariantAvailable ? 'https://schema.org/LimitedAvailability' : 'https://schema.org/OutOfStock',
            url: `https://www.hautehijab.com/products/${page.shopifyProduct.handle}`,
          }
        })}
        sku={page.shopifyProduct.variants?.edges[0].node.sku || undefined}
      />
      <style jsx global>
        {`
          @media (max-width: 767px) {
            #cookiescript_badge {
              display: none !important;
            }
          }
        `}
      </style>
      <ProductPage page={page} reviewData={reviewData}/>
    </>
  );
}

export async function getStaticPaths(props: GetStaticPathsContext) {
  // NOTE: no longer pre rendering products to avoid long build times
  // const product_pages = await getAllProductsWithSlug();
  // getLocalePaths(product_pages, locales),
  return {
    paths: [],  
    fallback: 'blocking'
  };
}

export async function getStaticProps({
  params,
  preview = false,
  locale = 'en-US',
}: GetStaticPropsContext) {
  const slug = String(params?.slug || '')
    .split('/')
    .reverse()[0];

  const [site, dictionary, page] = await Promise.all([getSite({ preview, locale }), getDictionary({ preview, locale }), getProduct({
    slug,
    preview,
    locale,
  })])
  
  return {
    props: {
      preview,
      site,
      dictionary,
      locale,
      page: page || null,
      slug,
    },
    revalidate: 300,
  }
}
